/**
 * Free gifts: keeps the cart in sync with what the discount functions will make free.
 *
 * Shopify Functions cannot add merchandise to a cart - `CartOperation` only offers
 * productDiscountsAdd, orderDiscountsAdd and the entered-code accept/reject pair. So the
 * split is: this file decides what is IN the cart, the functions decide what is FREE.
 *
 * THE ONE INVARIANT: the condition below must match the functions exactly. If this file
 * adds a gift the functions won't discount, the customer is charged for something the
 * page called free. That is the only way this can cost money, so the condition is stated
 * once, here, and mirrors:
 *
 *   gift-with-subscription   one set of gifts per showerhead unit bought on a selling
 *                            plan (2 subscription heads = 2 of each gift), plus one set
 *                            per bundle (distinct _bundle ids on one-time showerheads,
 *                            products in `bundleProductIds` of its afina.config)
 *   bogo-with-gifts          gifts only with a subscription too (`requireSubscription`
 *                            in every BOGO discount's afina.config metafield), and only
 *                            ONE set however many pairs - so a BOGO code caps it at 1
 *
 * There is NO "2+ one-time showerheads earn gifts" rule in any live discount. Adding
 * gifts on that basis charged $59 for them at checkout.
 *
 * Adding gifts as ONE-TIME lines is also load-bearing: subscriptions renew only lines
 * carrying a selling plan, so a one-time gift simply doesn't exist on cycle 2. Both
 * functions additionally refuse to discount a gift line that has a selling plan.
 */
(function () {
  'use strict';

  /* Products that count toward a BOGO pair AND earn the gifts.
     Quantities POOL across them: 1 showerhead + 1 handheld is a pair. */
  var QUALIFYING_PRODUCT_IDS = [
    8079741747377, // Afina Filtered Shower Head
    8961604714673, // Afina Handheld Filtered Showerhead
  ];

  /* The same showerheads by handle: the subscription products and their separate
     one-time products (the one-time purchase link adds those), so every way of buying
     a showerhead counts. */
  var QUALIFYING_HANDLES = [
    'filtered-showerhead',
    'handheld-filtered-showerhead',
    'afina-filtered-shower-head',
    'afina-handheld-filtered-showerhead',
  ];

  /** The free gifts (one-time lines, one set per subscription showerhead). Same two variants the product pages
      push with a subscription: Shower Steamers + 360° Shower Phone Holder.
      This list was missing, so every reconcile threw and gifts were never removed when
      the subscription showerhead left the cart. */
  var GIFTS = [
    { id: 46486633545905, name: 'Shower Steamers' },
    { id: 46486625648817, name: '360° Shower Phone Holder' },
  ];

  /** Gifts are a US-market offer. The functions enforce this too - this half only
      keeps non-US carts clean, so nobody sees a gift appear and then get charged. */
  var GIFT_MARKETS = ['US'];

  /** Codes of the BOGO discounts (bogo-with-gifts). A cart carrying one gets a single set
      of gifts: the offer is buy one get one plus one set of gifts, not one set per head. */
  var BOGO_CODES = ['AFINABOGO', 'BOGOLIVE'];

  /** Remembering a manual removal is per-tab and deliberately not persisted. */
  var REMOVED_KEY = 'afina:gifts-removed';

  var inFlight = false;

  /** Shopify.routes.root carries the market/locale prefix. Fall back rather than throw. */
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * Dawn declares `const PUB_SUB_EVENTS` at the top level of a classic script. That
   * creates a global LEXICAL binding, which is reachable by name but is NOT a property
   * of window - so `window.PUB_SUB_EVENTS` is undefined and any guard written against
   * it silently never fires.
   */
  function pubSubEvents() {
    try {
      if (typeof PUB_SUB_EVENTS !== 'undefined') return PUB_SUB_EVENTS;
    } catch (e) {}
    return window.PUB_SUB_EVENTS || null;
  }

  function log() {
    if (window.AFINA_GIFTS_DEBUG) console.log.apply(console, ['[free-gifts]'].concat([].slice.call(arguments)));
  }

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function inGiftMarket() {
    var country = window.Shopify && window.Shopify.country;
    return GIFT_MARKETS.indexOf(country) !== -1;
  }

  function hasId(list, id) { return list.indexOf(id) !== -1; }

  /** Mirrors the discount functions (see the header): a showerhead on a selling plan. */
  function isShowerhead(item) {
    return hasId(QUALIFYING_PRODUCT_IDS, item.product_id) || QUALIFYING_HANDLES.indexOf(item.handle) !== -1;
  }

  function hasBogoCode(cart) {
    return (cart.discount_codes || []).some(function (d) {
      return d.applicable !== false && BOGO_CODES.indexOf(String(d.code).toUpperCase()) !== -1;
    });
  }

  /** The bundles in the cart: distinct _bundle ids on one-time lines
      (sections/el-bundle-main.liquid tags every line of a bundle with one id,
      whether its unit is the showerhead or the Yearly Bundle). */
  function bundleCount(cart) {
    var ids = {};
    cart.items.forEach(function (item) {
      var id = item.properties && item.properties._bundle;
      if (id && !item.selling_plan_allocation) ids[id] = true;
    });
    return Object.keys(ids).length;
  }

  /** How many of each gift the cart has earned: one per subscription showerhead unit
      plus one per bundle, or a single set on a BOGO order. */
  function giftSets(cart) {
    if (!inGiftMarket()) return 0;
    var units = cart.items.reduce(function (sum, item) {
      return isShowerhead(item) && item.selling_plan_allocation ? sum + item.quantity : sum;
    }, 0) + bundleCount(cart);
    if (units === 0) return 0;
    return hasBogoCode(cart) ? 1 : units;
  }

  function qualifies(cart) { return giftSets(cart) > 0; }

  function giftLines(cart) {
    var ids = GIFTS.map(function (g) { return g.id; });
    return cart.items.filter(function (item) { return ids.indexOf(item.id) !== -1; });
  }

  function wasRemovedByHand() {
    try { return sessionStorage.getItem(REMOVED_KEY) === '1'; } catch (e) { return false; }
  }

  function rememberRemoval() {
    try { sessionStorage.setItem(REMOVED_KEY, '1'); } catch (e) {}
  }

  function forgetRemoval() {
    try { sessionStorage.removeItem(REMOVED_KEY); } catch (e) {}
  }

  function addGifts(missing, quantity) {
    // One request per gift, so an out-of-stock steamer doesn't also lose the holder.
    return Promise.all(missing.map(function (gift) {
      return fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: gift.id, quantity: quantity }] }),
      }).then(function (r) {
        if (!r.ok) log('could not add', gift.name, '- probably out of stock');
        return r.ok;
      }).catch(function () { return false; });
    }));
  }

  function removeLines(lines) {
    // Sequential: /cart/change.js invalidates line keys, so parallel removals race.
    return lines.reduce(function (chain, line) {
      return chain.then(function () {
        return fetch(root() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        }).catch(function () {});
      });
    }, Promise.resolve());
  }

  /** The BOGO page (el-bogo-main) adds its own single set of gifts, then applies its code
      on the way to checkout. Reconciling in between - before the code is on the cart -
      would read the order as plain subscriptions and add a set per head. */
  function onBogoPage() {
    return Boolean(document.querySelector('[data-el-bogo]'));
  }

  function reconcile() {
    if (inFlight || onBogoPage()) return Promise.resolve(false);
    inFlight = true;

    return getCart().then(function (cart) {
      var sets = giftSets(cart);
      var present = giftLines(cart);

      if (sets === 0) {
        // Gifts left behind after the showerhead is removed would be charged for.
        if (present.length === 0) { forgetRemoval(); return false; }
        log('no longer earned - removing', present.length, 'gift line(s)');
        return removeLines(present).then(function () { forgetRemoval(); return true; });
      }

      // Someone deleted a gift on purpose. Putting it straight back is hostile.
      if (wasRemovedByHand()) return false;

      // A gift bought on a selling plan is not discounted by either function, so it goes,
      // and is re-added below as a plain one-time line rather than being charged.
      // Of the rest, one line per gift at exactly `sets` units: more would be charged for,
      // fewer short-changes a customer who bought several heads.
      var seen = {}, drop = [], resize = [];
      present.forEach(function (line) {
        if (line.selling_plan_allocation || seen[line.id]) { drop.push(line); return; }
        seen[line.id] = true;
        if (line.quantity !== sets) resize.push(line);
      });
      var missing = GIFTS.filter(function (g) { return !seen[g.id]; });

      if (drop.length === 0 && resize.length === 0 && missing.length === 0) return false;
      log('gifts: want', sets, 'of each');

      return removeLines(drop)
        .then(function () {
          return resize.reduce(function (chain, line) {
            return chain.then(function () {
              return fetch(root() + 'cart/change.js', {
                method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: line.key, quantity: sets }),
              }).catch(function () {});
            });
          }, Promise.resolve());
        })
        .then(function () { return missing.length ? addGifts(missing, sets) : []; })
        .then(function () { return true; });
    }).catch(function (e) {
      log('reconcile failed', e);
      return false;
    }).then(function (changed) {
      inFlight = false;
      return changed;
    });
  }

  /**
   * Redraw the cart drawer the way its own remove/quantity controls do: swap the whole
   * .drawer__inner, toggle the empty state and refresh the cart icon. The drawer's pubsub
   * handler only swaps the item list and footer, which don't exist once the cart is empty,
   * so after the last gift went the drawer was left half drawn.
   */
  function redrawDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return null;
    return fetch(root() + '?sections=cart-drawer,cart-icon-bubble', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var parse = function (html) { return new DOMParser().parseFromString(html, 'text/html'); };
        if (sections['cart-drawer']) {
          var doc = parse(sections['cart-drawer']);
          var fresh = doc.querySelector('.drawer__inner');
          var current = drawer.querySelector('.drawer__inner');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
          var freshDrawer = doc.querySelector('cart-drawer');
          drawer.classList.toggle('is-empty', Boolean(freshDrawer && freshDrawer.classList.contains('is-empty')));
          var overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
        }
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble && sections['cart-icon-bubble']) {
          var section = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
          if (section) bubble.innerHTML = section.innerHTML;
        }
      })
      .catch(function (e) { log('redraw failed', e); });
  }

  /** Ask the theme to redraw. */
  function refreshUI() {
    if (document.body.classList.contains('template-cart')) { window.location.reload(); return Promise.resolve(); }
    var drawn = redrawDrawer();
    if (drawn) return drawn;
    var events = pubSubEvents();
    if (typeof window.publish === 'function' && events && events.cartUpdate) {
      return getCart().then(function (cart) {
        window.publish(events.cartUpdate, { source: 'afina-free-gifts', cartData: cart });
      });
    }
    // No pubsub on this page - a reload is heavy-handed but correct.
    if (document.body.classList.contains('template-cart')) window.location.reload();
    return Promise.resolve();
  }

  var pending = null;

  /** Cart writes arrive in bursts; settle before reading. */
  function schedule() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      reconcile().then(function (changed) { if (changed) refreshUI(); });
    }, 150);
  }

  function run() {
    reconcile().then(function (changed) { if (changed) refreshUI(); });
  }

  // Manual removal of a gift is remembered so it isn't re-added under the customer.
  document.addEventListener('click', function (event) {
    var remove = event.target.closest && event.target.closest('cart-remove-button, [data-afina-gift-remove]');
    if (!remove) return;
    var row = remove.closest('[data-afina-gift], .cart-item');
    if (row && row.getAttribute('data-afina-gift') !== null) rememberRemoval();
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  var events = pubSubEvents();
  if (typeof window.subscribe === 'function' && events && events.cartUpdate) {
    window.subscribe(events.cartUpdate, function (event) {
      if (event && event.source === 'afina-free-gifts') return; // don't chase our own tail
      schedule();
    });
  }

  /**
   * Backstop. The PDP is built in Replo and the lander has its own script, neither of
   * which is obliged to publish Dawn's pubsub event. Watching the cart endpoints
   * themselves works whatever framework performed the add.
   */
  (function watchCartRequests() {
    var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    var nativeFetch = window.fetch;
    if (typeof nativeFetch !== 'function') return;

    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isCartWrite = CART_WRITE.test(url);
      return nativeFetch.apply(this, arguments).then(function (response) {
        // inFlight means the write was ours, so reacting would loop.
        if (isCartWrite && !inFlight && response && response.ok) schedule();
        return response;
      });
    };
  })();

  window.AfinaFreeGifts = { reconcile: reconcile, refresh: refreshUI, qualifies: qualifies, giftSets: giftSets, inGiftMarket: inGiftMarket };
})();
